// Typed schema.org JSON-LD builders — single source for all structured data.
// Everything flows through <Seo jsonLd={[...]}>. NEVER add aggregateRating or
// fabricated stats here (SEO_MASTER_PLAN hard rule #2).
import { SITE_ORIGIN } from './routes'

export const AGENT_ID = `${SITE_ORIGIN}/#agent`

const TOWNS_SERVED = [
  'Lewes',
  'Rehoboth Beach',
  'Dewey Beach',
  'Bethany Beach',
  'Fenwick Island',
  'Milton',
] as const

export function realEstateAgentSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    '@id': AGENT_ID,
    name: 'Merritt & Shawn | Coastal Delaware Real Estate',
    alternateName: 'Merritt Cooch & Shawn McDonnell',
    url: `${SITE_ORIGIN}/`,
    image: `${SITE_ORIGIN}/media/team/merritt-shawn-coastal.webp`,
    telephone: '+1-302-645-2207',
    email: 'shawn@jacklingo.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '1240 Kings Highway',
      addressLocality: 'Lewes',
      addressRegion: 'DE',
      postalCode: '19958',
      addressCountry: 'US',
    },
    geo: { '@type': 'GeoCoordinates', latitude: 38.7746, longitude: -75.1554 },
    areaServed: TOWNS_SERVED.map((name) => ({
      '@type': 'Place',
      name,
      address: { '@type': 'PostalAddress', addressRegion: 'DE', addressCountry: 'US' },
    })),
    parentOrganization: {
      '@type': 'RealEstateAgent',
      name: 'Jack Lingo REALTOR®',
      url: 'https://www.jacklingo.com',
    },
    // TODO-HUMAN (Phase 9): confirm sameAs matches live social profiles.
    sameAs: [
      'https://www.instagram.com/merrittandshawn/',
      'https://www.linkedin.com/in/shawn-mcdonnell-41587513b',
      'https://www.linkedin.com/in/merritt-cooch-2496bb125',
    ],
  }
}

export function webSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Merritt & Shawn | Coastal Delaware Real Estate',
    url: `${SITE_ORIGIN}/`,
    publisher: { '@id': AGENT_ID },
  }
}

export function personSchema(person: {
  name: string
  jobTitle: string
  email: string
  telephone: string
  sameAs: string[]
  knowsAbout: string[]
  description: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: person.name,
    jobTitle: person.jobTitle,
    email: person.email,
    telephone: person.telephone,
    worksFor: { '@id': AGENT_ID },
    sameAs: person.sameAs,
    knowsAbout: person.knowsAbout,
    description: person.description,
  }
}

export const SHAWN = personSchema({
  name: 'Shawn McDonnell',
  jobTitle: 'Associate Broker',
  email: 'shawn@jacklingo.com',
  telephone: '+1-302-542-8591',
  sameAs: ['https://www.linkedin.com/in/shawn-mcdonnell-41587513b'],
  knowsAbout: [
    'Historic home restoration',
    'New construction',
    'Resort and second-home real estate',
    'Delaware coastal real estate',
  ],
  description:
    'Associate Broker with Jack Lingo REALTOR® in Lewes, Delaware, pairing a builder’s and restorer’s eye with deep local roots at the Delaware beaches.',
})

export const MERRITT = personSchema({
  name: 'Merritt Cooch',
  jobTitle: 'REALTOR®',
  email: 'merritt@jacklingo.com',
  telephone: '+1-302-562-7954',
  sameAs: ['https://www.linkedin.com/in/merritt-cooch-2496bb125'],
  knowsAbout: [
    'Delaware coastal real estate',
    'Second homes and relocation',
    'Sussex County Delaware neighborhoods',
  ],
  description:
    'REALTOR® with Jack Lingo REALTOR® in Lewes, Delaware — an 11th-generation Delawarean helping buyers and sellers across the Delaware beaches.',
})

export function breadcrumbSchema(items: { label: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{ label: 'Home', path: '/' }, ...items].map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.label,
      item: `${SITE_ORIGIN}${item.path}`,
    })),
  }
}

export function faqSchema(qa: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qa.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  }
}

export function serviceSchema(serviceType: string, description: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType,
    description,
    provider: { '@id': AGENT_ID },
    areaServed: TOWNS_SERVED.map((name) => ({
      '@type': 'Place',
      name,
      address: { '@type': 'PostalAddress', addressRegion: 'DE', addressCountry: 'US' },
    })),
  }
}

export function placeSchema(place: {
  name: string
  latitude: number
  longitude: number
  description?: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Place',
    name: place.name,
    ...(place.description ? { description: place.description } : {}),
    geo: {
      '@type': 'GeoCoordinates',
      latitude: place.latitude,
      longitude: place.longitude,
    },
    containedInPlace: {
      '@type': 'AdministrativeArea',
      name: 'Sussex County, Delaware',
      containedInPlace: { '@type': 'State', name: 'Delaware' },
    },
  }
}
